import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { UNIT_TYPES } from 'constants/product'
import { ApiErrors } from 'ui-components/ApiErrors'
import { Form as FormComponent } from 'components/Form'
import { currencyFormat, decimalForInput } from 'utils/format'
import { requestManufacturers } from 'requests/manufacturers'
import { requestProductCategories } from 'requests/product-categories'
import { requestProductGroups } from 'requests/product-groups'
import {
  TitleForm,
  SubTitleForm,
  DescriptionForm,
  InputForm,
  SelectForm,
} from 'components/Form/components'
import {
  ThreeFields,
  TwoFields,
} from './styles'

export default function Form(props) {
  const {
    product,
    onCancel,
    onSave,
    responseError,
    saving,
    isNewProduct,
  } = props

  const [attributes, setAttributes] = useState()
  const [selectedManufacturer, setSelectedManufacturer] = useState()
  const [selectedCategory, setSelectedCategory] = useState()
  const [selectedGroup, setSelectedGroup] = useState()
  const [selectedUnitType, setSelectedUnitType] = useState()
  const [manufacturers, setManufacturers] = useState([])
  const [categories, setCategories] = useState([])
  const [groups, setGroups] = useState([])

  const getManufacturers = useCallback(async () => {
    const response = await requestManufacturers()
    if (response.error) return setManufacturers(response)

    const responseOptions = response.data.map((item) => ({
      value: item.id.toString(), label: item.attributes.name,
    }))

    setManufacturers(responseOptions)
    return setSelectedManufacturer({
      value: product.attributes.manufacturer?.id,
      label: product.attributes.manufacturer?.name,
    })
  }, [product])

  const getCategories = useCallback(async () => {
    const response = await requestProductCategories()
    if (response.error) return setCategories(response)

    const options = response.data.map((item) => ({
      value: item.id.toString(), label: item.attributes.name,
    }))

    setCategories(options)
    return setSelectedCategory({
      value: product.attributes.category?.id,
      label: product.attributes.category?.name,
    })
  }, [product])

  const getGroups = useCallback(async () => {
    const response = await requestProductGroups()
    if (response.error) return setGroups(response)

    const options = response.data.map((item) => ({
      value: item.id.toString(), label: item.attributes.name,
    }))

    setGroups(options)
    return setSelectedGroup({
      value: product.attributes.group?.id,
      label: product.attributes.group?.name,
    })
  }, [product])

  const setUnitType = useCallback(() => {
    if (product.attributes.unit_type) {
      const selected = UNIT_TYPES.find((item) => (
        item.value === product.attributes.unit_type
      ))

      setSelectedUnitType(selected)
    }
  }, [product])

  useEffect(() => {
    getManufacturers()
    getCategories()
    getGroups()
    setUnitType()
  }, [setUnitType, getManufacturers, getCategories, getGroups])

  useEffect(() => {
    setAttributes({ ...product.attributes })
  }, [product])

  const updateAttributes = (name, value) => {
    setAttributes({
      ...attributes,
      [name]: value,
    })
  }

  const handleChange = (evt) => {
    const { value, name } = evt.target
    updateAttributes(name, value)
  }

  const handleChangeStock = (evt) => {
    const { value, name } = evt.target

    setAttributes({
      ...attributes,
      product_stock_attributes: {
        ...attributes.product_stock_attributes,
        [name]: value,
      },
    })
  }

  const handleCurrencyChange = (evt) => {
    const { value, name } = evt.target
    const number = decimalForInput(value)
    updateAttributes(name, number)
  }

  const handleChangeSelect = (option, type = 'object') => {
    const attr = type !== 'object'
      ? option.value
      : { id: option.value, name: option.label }

    updateAttributes(option.name, attr)
  }

  const handleSave = () => {
    onSave(attributes)
  }

  return attributes ? (
    <FormComponent
      onCancel={onCancel}
      onSave={handleSave}
      saving={saving}
    >
      <TitleForm>Produto</TitleForm>

      <ApiErrors responseError={responseError} />

      <InputForm
        fullWidth
        label="Nome"
        type="text"
        name="name"
        value={attributes.name}
        onChange={handleChange}
      />

      <InputForm
        fullWidth
        label="Descrição"
        type="text"
        name="description"
        value={attributes.description}
        onChange={handleChange}
      />

      <ThreeFields>
        <InputForm
          fullWidth
          label="Preço total de venda"
          type="text"
          name="selling_price"
          value={currencyFormat(attributes.selling_price)}
          onChange={handleCurrencyChange}
        />
        <InputForm
          fullWidth
          label="Margem de lucro"
          type="text"
          name="net_profit"
          value={currencyFormat(attributes.net_profit)}
          onChange={handleCurrencyChange}
        />
        <InputForm
          fullWidth
          label="Garantia"
          type="text"
          name="warranty"
          value={attributes.warranty}
          onChange={handleChange}
        />
      </ThreeFields>

      <SelectForm
        options={manufacturers}
        value={selectedManufacturer}
        onChange={handleChangeSelect}
        label="Fabricante"
        name="manufacturer"
      />

      <SubTitleForm>Classificação</SubTitleForm>

      <TwoFields>
        <SelectForm
          options={categories}
          value={selectedCategory}
          onChange={handleChangeSelect}
          label="Categoria"
          name="category"
        />
        <SelectForm
          options={groups}
          value={selectedGroup}
          onChange={handleChangeSelect}
          label="Grupo"
          name="group"
        />
      </TwoFields>

      <SubTitleForm>Códigos</SubTitleForm>

      <DescriptionForm>
        Todos os códigos abaixo serão utilizados na busca
      </DescriptionForm>

      <ThreeFields>
        <InputForm
          fullWidth
          label="Código interno"
          type="text"
          name="bar_code"
          value={attributes.bar_code}
          onChange={handleChange}
        />
        <InputForm
          fullWidth
          label="Código do fornecedor"
          type="text"
          name="original_code"
          value={attributes.original_code}
          onChange={handleChange}
        />
        <InputForm
          fullWidth
          label="Código de busca"
          type="text"
          name="custom_search_parameters"
          value={attributes.custom_search_parameters}
          onChange={handleChange}
        />
      </ThreeFields>

      <SubTitleForm>Estoque</SubTitleForm>

      <DescriptionForm>
        Produtos sem controle de estoque, deixe a quantidade mínima em zero
      </DescriptionForm>

      <TwoFields>
        { isNewProduct && (
          <InputForm
            fullWidth
            label="Qtd. atual"
            type="text"
            name="quantity"
            value={attributes.product_stock_attributes.quantity}
            onChange={handleChangeStock}
          />
        )}

        <InputForm
          fullWidth
          label="Qtd. mínima"
          type="text"
          name="minimum_quantity"
          value={attributes.product_stock_attributes.minimum_quantity}
          onChange={handleChangeStock}
        />
      </TwoFields>

      <TwoFields>
        <SelectForm
          options={UNIT_TYPES}
          value={selectedUnitType}
          onChange={(option) => handleChangeSelect(option, 'string')}
          label="Medida da unidade"
          name="unit_type"
        />
        <InputForm
          fullWidth
          label="Qtd. por embalagem"
          type="text"
          name="unit_quantity"
          value={attributes.unit_quantity}
          onChange={handleChange}
        />
      </TwoFields>
    </FormComponent>
  ) : null
}

Form.propTypes = {
  product: PropTypes.objectOf(PropTypes.any),
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  responseError: PropTypes.objectOf(PropTypes.any),
  saving: PropTypes.bool,
  isNewProduct: PropTypes.bool,
}

Form.defaultProps = {
  product: { attributes: {} },
  responseError: {},
  saving: false,
  isNewProduct: false,
}
